import React, {
  createContext,
  useState,
  useContext,
  ReactNode,
  useEffect,
  useMemo,
  useCallback,
} from 'react';
import { ThemeProvider, createTheme, Theme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import '@brightmls/globalcss/lib/css/global_styles.css';
import { useBrand } from './BrandingContext';

// see docs about module augmentation to add custom colors to the Palette and Theme
// https://mui.com/material-ui/customization/palette/#typescript
declare module '@mui/material/styles' {
  interface Theme {
    states: {
      readonly: string;
    };
  }
  // allow configuration using `createTheme()`
  interface ThemeOptions {
    states?: {
      readonly?: string;
    };
  }

  // Add a new color to the existing palettes
  interface PaletteColor {
    medium?: string;
  }
  interface SimplePaletteColorOptions {
    medium?: string;
  }

  // Add a new palette to the theme
  interface PaletteOptions {
    states: {
      readonly: string;
      disabled: string;
      default: string;
      focus: string;
    };
    buttons?: object;
  }
  interface Palette {
    states: {
      readonly: string;
      disabled: string;
      default: string;
      focus: string;
    };
  }

  // add new color to the background Palette, specifically
  interface TypeBackground {
    secondary: string;
    pale: string;
    gradient: string;
  }
}

// Define base brand colors and other theme constants
export const defaultBrandColors = {
  primary: {
    main: '#F13C00', // Primary button color from Buttons.ts
    light: '#ff6e3d',
    dark: '#652106',
    contrastText: '#ffffff',
  },
  secondary: {
    main: '#1f3473', // Moving the blue to secondary
    light: '#4c619c',
    medium: '#253772',
    dark: '#15254f',
    contrastText: '#ffffff',
  },
  error: {
    main: '#D93A3A',
    light: '#e66767',
    medium: '#CE1C1F',
    dark: '#b22020',
    contrastText: '#ffffff',
  },
  info: {
    main: '#0288D1',
    dark: '#1f3473',
    light: '#E5F6FD',
    contrastText: '#000000',
  },
  warning: {
    main: '#DECB6D',
    light: '#FFFAE7',
    dark: '#9A8100',
    contrastText: '#000000',
  },
  success: {
    main: '#477E39',
    light: '#6ba55b',
    dark: '#2f5725',
    contrastText: '#ffffff',
  },
  grey: {
    lightest: '#FAFAFA',
    50: '#F5F5F5',
    100: '#EBEBEB',
    200: '#D9D9D9',
    300: '#BBBBC4',
    400: '#A8A8A9',
    500: '#767678',
    600: '#48484A',
    700: '#1A1A1E',
  },
  // Add stateful colors
  states: {
    readonly: '#E4E4E4',
    disabled: '#A8A8A9',
    default: '#FFFFFF',
    focus: '#8094D6', // Add focus color to brand colors
  },
  buttons: {
    crop: {
      textColor: '#FEE8E6',
    },
  },
  // Background colors
  background: {
    default: '#ffffff',
    paper: '#ffffff',
    secondary: '#e8edf6',
    pale: '#F6F9FE', // A pale background, found across the form for "almost-white" callouts
    gradient:
      'linear-gradient(214deg, #FCFCFB 17%, #DBF6F2 60.21%, #86E2D5 109.63%)', // Create page gradient
  },
};

// Create component overrides dynamically based on provided colors
const createComponentOverrides = (colors: typeof defaultBrandColors) => {
  // Helper function to create transparent colors
  const withOpacity = (color: string, opacity: number) => {
    // For simple hex colors (#RRGGBB), convert to rgba
    if (color.startsWith('#') && (color.length === 7 || color.length === 4)) {
      let r, g, b;
      if (color.length === 7) {
        r = parseInt(color.substring(1, 3), 16);
        g = parseInt(color.substring(3, 5), 16);
        b = parseInt(color.substring(5, 7), 16);
      } else {
        r = parseInt(color.substring(1, 2), 16) * 17;
        g = parseInt(color.substring(2, 3), 16) * 17;
        b = parseInt(color.substring(3, 4), 16) * 17;
      }
      return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    }
    return color; // If not a simple hex, return as is
  };

  return {
    MuiRadio: {
      styleOverrides: {
        root: {
          '&.Mui-checked': {
            color: colors.secondary.main,
          },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          '& .Mui-checked': {
            '& .MuiSwitch-thumb': {
              backgroundColor: colors.secondary.main,
            },
            '& + .MuiSwitch-track': {
              backgroundColor: `${colors.secondary.main} !important`,
              opacity: 0.5,
            },
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          '&.Mui-checked': {
            color: colors.secondary.main,
          },
        },
      },
    },
    MuiDateCalendar: {
      styleOverrides: {
        root: {
          height: '300px !important',
        },
      },
    },
    MuiYearCalendar: {
      styleOverrides: {
        root: {
          paddingRight: '24px !important',
        },
      },
    },
    MuiPickersDay: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: `${colors.secondary.main} !important`,
            '&:hover': {
              backgroundColor: `${colors.secondary.dark} !important`,
            },
            '&:focus': {
              backgroundColor: colors.secondary.main,
            },
          },
          '&.MuiPickersDay-today': {
            borderColor: colors.secondary.main,
            color: colors.secondary.main,
            '&:hover': {
              backgroundColor: `${colors.secondary.main} !important`,
              color: `${colors.primary.contrastText} !important`,
            },
          },
        },
      },
    },
    MuiPickersTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            '&.Mui-focused': {
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: `${colors.states.focus} !important`,
                borderWidth: '2px', // Updated to 2px
              },
            },
            '&.Mui-readOnly': {
              backgroundColor: colors.states.readonly,
              '&.Mui-focused': {
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: `${colors.states.focus} !important`,
                  borderWidth: '2px', // Updated to 2px
                },
              },
            },
            '&.Mui-disabled': {
              backgroundColor: colors.states.disabled,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: colors.states.focus,
            },
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          '&.MuiPickersCalendarHeader-iconButton': {
            color: colors.secondary.main,
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        containedPrimary: {
          backgroundColor: colors.primary.main,
          '&:hover': {
            backgroundColor: colors.primary.dark,
          },
          '&.Mui-disabled': {
            backgroundColor: colors.grey[300], // Using branding color instead of hardcoded '#BBBBC4'
          },
        },
        outlinedPrimary: {
          borderColor: colors.primary.main,
          color: colors.primary.main,
          '&:hover': {
            borderColor: colors.primary.main,
            color: colors.primary.dark,
          },
          '&.Mui-disabled': {
            borderColor: colors.grey[300], // Using branding color instead of hardcoded '#BBBBC4'
            color: colors.grey[300], // Using branding color instead of hardcoded '#BBBBC4'
          },
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: colors.secondary.main,
            color: '#fff',
            '&:hover': {
              backgroundColor: colors.secondary.dark,
            },
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          '&.Mui-focused': {
            borderColor: colors.states.focus,
          },
        },
      },
    },
    MuiMenu: {
      defaultProps: {
        PaperProps: {
          sx: {
            maxHeight: '300px',
            overflowY: 'auto',
          },
        },
      },
    },
    MuiSelect: {
      defaultProps: {
        MenuProps: {
          PaperProps: {
            sx: {
              maxHeight: '300px',
            },
          },
        },
      },
      styleOverrides: {
        root: {
          backgroundColor: colors.states.default,
          '&.Mui-focused': {
            backgroundColor: colors.states.default,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: `${colors.states.focus} !important`,
              borderWidth: '2px', // Updated to 2px
            },
          },
          '&.Mui-readOnly': {
            backgroundColor: colors.states.readonly,
            '&.Mui-focused': {
              backgroundColor: colors.states.readonly,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: `${colors.states.focus} !important`,
                borderWidth: '2px', // Updated to 2px
              },
            },
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: colors.states.focus,
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          '&.MuiChip-filled': {
            backgroundColor: withOpacity(colors.grey[700], 0.08), // Using color with opacity instead of rgba
          },
          '& .MuiChip-deleteIcon': {
            color: withOpacity(colors.grey[500], 0.62), // Using color with opacity instead of rgba
            '&:hover': {
              color: colors.grey[500],
            },
          },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: withOpacity(colors.secondary.main, 0.08), // Using branding color with opacity
            '&:hover': {
              backgroundColor: withOpacity(colors.secondary.main, 0.08), // Using branding color with opacity
            },
          },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: withOpacity(colors.secondary.main, 0.08), // Using branding color with opacity
            '&:hover': {
              backgroundColor: withOpacity(colors.secondary.main, 0.08), // Using branding color with opacity
            },
          },
          '&:hover': {
            backgroundColor: withOpacity(colors.secondary.main, 0.08), // Using branding color with opacity
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: colors.states.default,
          '&.Mui-focused': {
            backgroundColor: colors.states.default,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: `${colors.states.focus} !important`,
              borderWidth: '1px',
            },
          },
          '&.Mui-disabled': {
            backgroundColor: colors.states.disabled,
          },
          '&.Mui-readOnly': {
            backgroundColor: colors.states.readonly,
            '&.Mui-focused': {
              backgroundColor: colors.states.readonly,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: `${colors.states.focus} !important`,
                borderWidth: '1px',
              },
            },
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: colors.states.focus,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: colors.states.focus,
            boxShadow: `0 0 0 2px ${withOpacity(colors.states.focus, 0.2)}`, // Using branding color with opacity
          },
          '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
            borderColor: withOpacity(colors.grey[700], 0.23), // Using branding color with opacity
          },
          '& .MuiInputBase-multiline': {
            padding: 1,
            minHeight: '100px',
          },
          '& .MuiInputBase-inputMultiline': {
            height: '100% !important',
            overflowY: 'auto !important',
            resize: 'none',
          },
        },
      },
    },

    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: colors.states.default,
            '&.Mui-focused': {
              backgroundColor: colors.states.default,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: `${colors.states.focus} !important`,
                borderWidth: '2px',
              },
            },
            '&.Mui-disabled': {
              backgroundColor: colors.states.disabled,
            },
            '&.Mui-readOnly': {
              backgroundColor: colors.states.readonly,
              '&.Mui-focused': {
                backgroundColor: colors.states.readonly,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: `${colors.states.focus} !important`,
                },
              },
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: colors.states.focus,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: colors.states.focus,
              borderWidth: '2px',
            },
          },
          '& .MuiInputBase-multiline': {
            padding: 1,
            minHeight: '100px',
          },
          '& .MuiInputBase-inputMultiline': {
            height: '100% !important',
            overflowY: 'auto !important',
            resize: 'none',
          },
        },
      },
    },
  };
};

// Create base theme configuration
const createBaseTheme = (
  mode: 'light' | 'dark',
  brandColors = defaultBrandColors,
) => {
  // Use the provided brandColors instead of the default ones
  return createTheme({
    palette: {
      mode,
      primary: brandColors.primary,
      secondary: brandColors.secondary,
      error: brandColors.error,
      info: brandColors.info,
      warning: brandColors.warning,
      success: brandColors.success,
      grey: brandColors.grey,
      text: {
        primary: brandColors.grey[700],
        secondary: brandColors.grey[500],
      },
      buttons: brandColors.buttons,
      background: brandColors.background,
      states: brandColors.states,
    },
    typography: {
      fontFamily: '"Roboto", "Inter", "Helvetica", "Arial", sans-serif',
      h1: { fontSize: '2rem', fontWeight: 600 },
      h2: { fontSize: '1.75rem', fontWeight: 600 },
      h3: { fontSize: '1.5rem', fontWeight: 600 },
      h4: { fontSize: '1.25rem', fontWeight: 600 },
      h5: { fontSize: '1.1rem', fontWeight: 600 },
      h6: { fontSize: '1rem', fontWeight: 600 },
      body1: { fontSize: '1rem' },
      body2: { fontSize: '0.875rem' },
    },
    components: createComponentOverrides(brandColors),
  });
};

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

export const CustomThemeProvider: React.FC<ThemeProviderProps> = ({
  children,
}) => {
  const { branding, isLoading } = useBrand();
  const [currentTheme, setCurrentTheme] = useState<'light' | 'dark'>('light');

  useEffect(() => {
    if (branding?.isdarktheme !== undefined) {
      setCurrentTheme(branding.isdarktheme ? 'dark' : 'light');
    }
  }, [branding?.isdarktheme]);

  const theme = useMemo(() => {
    if (!branding || isLoading) {
      return createBaseTheme(currentTheme);
    }

    // Merge default brand colors with custom branding palette
    const mergedBrandColors = {
      ...defaultBrandColors,
      primary: {
        ...defaultBrandColors.primary,
        ...(branding?.themeoptions?.palette?.primary || {}),
      },
      secondary: {
        ...defaultBrandColors.secondary,
        ...(branding?.themeoptions?.palette?.secondary || {}),
      },
      error: {
        ...defaultBrandColors.error,
        ...(branding?.themeoptions?.palette?.error || {}),
      },
      warning: {
        ...defaultBrandColors.warning,
        ...(branding?.themeoptions?.palette?.warning || {}),
      },
      success: {
        ...defaultBrandColors.success,
        ...(branding?.themeoptions?.palette?.success || {}),
      },
      // Don't override grey scale directly as it's an object with multiple values
      states: {
        ...defaultBrandColors.states,
        ...(branding?.themeoptions?.palette?.states || {}),
      },
    };

    const baseTheme = createBaseTheme(currentTheme, mergedBrandColors);

    const finalTheme = createTheme({
      ...baseTheme,
      palette: {
        ...baseTheme.palette,
        ...(branding?.themeoptions?.palette || {}),
      },
      typography: {
        ...baseTheme.typography,
        ...(branding?.themeoptions?.typography || {}),
      },
      shape: {
        ...baseTheme.shape,
        ...(branding?.themeoptions?.shape || {}),
      },
      breakpoints: {
        ...baseTheme.breakpoints,
        ...(branding?.themeoptions?.breakpoints || {}),
      },
      spacing: branding?.themeoptions?.spacing || baseTheme.spacing,
      zIndex: {
        ...baseTheme.zIndex,
        ...(branding?.themeoptions?.zIndex || {}),
      },
      transitions: {
        ...baseTheme.transitions,
        ...(branding?.themeoptions?.transitions || {}),
      },
    });

    // apply custom grey properties after final theme creation
    Object.keys(mergedBrandColors.grey).forEach((key) => {
      if (['lightest'].includes(key)) {
        (finalTheme.palette.grey as any)[key] = (mergedBrandColors.grey as any)[
          key
        ];
      }
    });

    return finalTheme;
  }, [branding, isLoading, currentTheme]);

  const toggleTheme = useCallback(() => {
    setCurrentTheme((prevTheme) => (prevTheme === 'light' ? 'dark' : 'light'));
  }, []);

  const themeProviderValue = useMemo(() => {
    return { theme, toggleTheme };
  }, [theme, toggleTheme]);

  return (
    <ThemeContext.Provider value={themeProviderValue}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};
